import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";

export interface Contract {
  id: string;
  tenant_id: string;
  deal_id: string | null;
  proposal_id: string | null;
  title: string;
  version: number;
  status: string;
  document_url: string | null;
  signature_url: string | null;
  signer_name: string | null;
  signer_email: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  signed_at: string | null;
  expires_at: string | null;
  signing_token: string | null;
  template_id: string | null;
  metadata: Json | null;
  created_at: string;
  updated_at: string;
  deals?: { title: string; amount: number | null } | null;
  proposals?: { title: string } | null;
}

export function useContracts() {
  return useQuery({
    queryKey: ["contracts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contracts")
        .select(`
          *,
          deals(title, amount),
          proposals(title)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as unknown as Contract[];
    },
  });
}

export function useContract(contractId: string) {
  return useQuery({
    queryKey: ["contracts", contractId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contracts")
        .select(`
          *,
          deals(title, amount, accounts(name)),
          proposals(title, content)
        `)
        .eq("id", contractId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!contractId,
  });
}

export function useCreateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contract: {
      deal_id?: string;
      proposal_id?: string;
      title: string;
      signer_name?: string;
      signer_email?: string;
      expires_at?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) {
        throw new Error("No tenant assigned");
      }

      const { data, error } = await supabase
        .from("contracts")
        .insert({ ...contract, tenant_id: profile.tenant_id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contract created successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, deals, proposals, metadata, ...updates }: Partial<Contract> & { id: string }) => {
      const updateData = metadata !== undefined ? { ...updates, metadata } : updates;
      const { data, error } = await supabase
        .from("contracts")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contract updated");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useSendContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, signer_email }: { id: string; signer_email: string }) => {
      const { data, error } = await supabase
        .from("contracts")
        .update({ 
          status: "sent", 
          sent_at: new Date().toISOString(),
          signer_email 
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contract sent for signature");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contracts")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contract deleted");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}
